import { z } from 'zod';
import {
  BOARD_TITLE_MAX_LENGTH,
  CARD_TITLE_MAX_LENGTH,
  SHORT_LINK_MAX_LENGTH,
  cardNumberSchema,
  cardReferenceSchema,
} from './board';
import { boardFilterQuerySchema } from './board-filter';
import { ulidSchema } from './id';
import { cardLabelSchema } from './label';
import { LIST_TITLE_MAX_LENGTH } from './list';
import { RANK_MAX_LENGTH } from './rank';
import { publicProfileSchema } from './user';

/**
 * Timeline view contracts — block B52.
 *
 * These are route-specific read models for `GET /boards/:id/timeline`.
 * Timeline is keyed by a half-open date range and can render cards with either
 * a start date, a due date, or both, so its entries stay separate from Calendar
 * and board-face card shapes.
 */

const timelineInstantQuerySchema = z.iso.datetime({ offset: true });

const timelineRangeQueryShape = {
  from: timelineInstantQuerySchema,
  to: timelineInstantQuerySchema,
} as const;

function isIncreasingRange(value: { from: string; to: string }): boolean {
  return Date.parse(value.from) < Date.parse(value.to);
}

const increasingRangeIssue = {
  message: 'from must be before to',
  path: ['to'],
};

export const timelineGroupBySchema = z.enum(['none', 'list', 'member', 'label']);

export type TimelineGroupBy = z.infer<typeof timelineGroupBySchema>;

export const timelineRangeQuerySchema = z
  .strictObject(timelineRangeQueryShape)
  .refine(isIncreasingRange, increasingRangeIssue)
  .describe('Timeline range query using half-open [from, to) semantics.');

export type TimelineRangeQuery = z.infer<typeof timelineRangeQuerySchema>;

export const boardTimelineQuerySchema = boardFilterQuerySchema
  .extend({
    ...timelineRangeQueryShape,
    groupBy: timelineGroupBySchema.default('none'),
  })
  .refine(isIncreasingRange, increasingRangeIssue);

export type BoardTimelineQuery = z.infer<typeof boardTimelineQuerySchema>;

export const timelineRangeSchema = z
  .object(timelineRangeQueryShape)
  .refine(isIncreasingRange, increasingRangeIssue);

export type TimelineRange = z.infer<typeof timelineRangeSchema>;

export const timelineBoardRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(BOARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  closed: z.boolean(),
});

export type TimelineBoardRef = z.infer<typeof timelineBoardRefSchema>;

export const timelineListRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(LIST_TITLE_MAX_LENGTH),
  position: z.string().max(RANK_MAX_LENGTH),
});

export type TimelineListRef = z.infer<typeof timelineListRefSchema>;

export const timelineCardRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(CARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  cardNumber: cardNumberSchema,
  /** `KEY-NNNN` for a viewer who may use the board's references, else `null` — `cardReferenceSchema` in `./board`. */
  ref: cardReferenceSchema.nullable(),
});

export type TimelineCardRef = z.infer<typeof timelineCardRefSchema>;

const timelineEntryDateSchema = z.string().min(1).nullable();

export const timelineCardEntrySchema = z
  .object({
    card: timelineCardRefSchema,
    list: timelineListRefSchema,
    startAt: timelineEntryDateSchema,
    dueAt: timelineEntryDateSchema,
    completed: z.boolean(),
  })
  .refine((entry) => entry.startAt !== null || entry.dueAt !== null, {
    message: 'startAt or dueAt is required',
    path: ['dueAt'],
  });

export type TimelineCardEntry = z.infer<typeof timelineCardEntrySchema>;

const timelineLaneBaseSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  entries: z.array(timelineCardEntrySchema),
});

export const timelineLaneSchema = z.discriminatedUnion('kind', [
  timelineLaneBaseSchema.extend({
    kind: z.literal('none'),
  }),
  timelineLaneBaseSchema.extend({
    kind: z.literal('list'),
    list: timelineListRefSchema,
  }),
  timelineLaneBaseSchema.extend({
    kind: z.literal('member'),
    member: publicProfileSchema.nullable(),
  }),
  timelineLaneBaseSchema.extend({
    kind: z.literal('label'),
    label: cardLabelSchema.nullable(),
  }),
]);

export type TimelineLane = z.infer<typeof timelineLaneSchema>;

export const boardTimelineSchema = z.object({
  board: timelineBoardRefSchema,
  range: timelineRangeSchema,
  groupBy: timelineGroupBySchema,
  lanes: z.array(timelineLaneSchema),
  unscheduledCardCount: z.number().int().nonnegative(),
});

export type BoardTimeline = z.infer<typeof boardTimelineSchema>;
